import React, { useState, useEffect } from 'react';
import './DayModal.css';

const DayModal = ({ date, elements = [], elements2 = [], dateAssociations, onSave, onClose, language }) => {
  // Fonction pour formater une date en chaîne ISO (YYYY-MM-DD)
  const formatDateToISO = (date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };
  
  // Extraction des données existantes pour cette date
  const dateKey = formatDateToISO(date);
  const existingData = dateAssociations[dateKey] || { elementIds: [], element2Ids: [], ratings: {}, notes: '' };
  
  // État local pour les données de la modale
  const [selectedElementIds, setSelectedElementIds] = useState(existingData.elementIds || []);
  const [selectedElement2Ids, setSelectedElement2Ids] = useState(existingData.element2Ids || []);
  const [ratings, setRatings] = useState(existingData.ratings || {});
  const [notes, setNotes] = useState(existingData.notes || '');
  
  // Traductions pour les textes de l'interface
  const translations = {
    fr: {
      months: [
        'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
        'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
      ],
      title: 'Éditer le jour',
      elementsSection: 'Éléments',
      notesSection: 'Notes',
      noElements: 'Aucun élément disponible',
      notesPlaceholder: 'Ajouter des notes pour ce jour...',
      save: 'Enregistrer',
      cancel: 'Annuler'
    },
    en: {
      months: [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ],
      title: 'Edit day',
      elementsSection: 'Elements',
      notesSection: 'Notes',
      noElements: 'No elements available',
      notesPlaceholder: 'Add notes for this day...',
      save: 'Save',
      cancel: 'Cancel'
    }
  };
  
  const t = translations[language];
  
  // Gestion de la sélection/désélection d'un élément
  const toggleElement = (elementId) => {
    if (selectedElementIds.includes(elementId)) {
      setSelectedElementIds(selectedElementIds.filter(id => id !== elementId));
    } else {
      setSelectedElementIds([...selectedElementIds, elementId]);
    }
  };
  
  // Gestion de la sélection/désélection d'un élément2
  const handleToggleElement2 = (elementId) => {
    if (selectedElement2Ids.includes(elementId)) {
      setSelectedElement2Ids(selectedElement2Ids.filter(id => id !== elementId));
      const newRatings = { ...ratings };
      delete newRatings[elementId];
      setRatings(newRatings);
    } else {
      setSelectedElement2Ids([...selectedElement2Ids, elementId]);
      setRatings({ ...ratings, [elementId]: 1 });
    }
  };

  const handleRatingChange = (elementId, value) => {
    setRatings({ ...ratings, [elementId]: parseInt(value, 10) });
  };
  
  // Gestion de la soumission du formulaire
  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(date, {
      elementIds: selectedElementIds,
      element2Ids: selectedElement2Ids,
      ratings: ratings,
      notes: notes
    });
  };
  
  // Formater la date pour l'affichage
  const formattedDate = `${date.getDate()} ${t.months[date.getMonth()]} ${date.getFullYear()}`;
  
  // Gestion du clic à l'extérieur de la modale pour la fermer
  const handleOutsideClick = (e) => {
    if (e.target.classList.contains('modal-overlay')) {
      onClose();
    }
  };
  
  return (
    <div className="modal-overlay" onClick={handleOutsideClick}>
      <div className="modal-content">
        <div className="modal-header">
          <h2>{t.title}: {formattedDate}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="modal-section">
            <h3>{t.elementsSection}</h3>
            {elements.length === 0 ? (
              <p className="no-elements">{t.noElements}</p>
            ) : (
              <div className="elements-selection">
                {elements.map(element => (
                  <div 
                    key={element.id} 
                    className={`element-checkbox ${selectedElementIds.includes(element.id) ? 'selected' : ''}`}
                    onClick={() => toggleElement(element.id)}
                  >
                    <div 
                      className="element-color" 
                      style={{ backgroundColor: element.couleur }}
                    ></div>
                    <span className="element-text">{element.texte}</span>
                    <div className="checkbox-indicator">
                      {selectedElementIds.includes(element.id) && '✓'}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="modal-section">
            <h3>{t.elementsSection} 2</h3>
            {elements2.length === 0 ? (
              <p className="no-elements">{t.noElements}</p>
            ) : (
              <div className="elements-selection">
                {elements2.map(element => (
                  <div 
                    key={element.id} 
                    className={`element-checkbox ${selectedElement2Ids.includes(element.id) ? 'selected' : ''}`}
                  >
                    <div className="element-checkbox-content" onClick={() => handleToggleElement2(element.id)}>
                      <div 
                        className="element-color" 
                        style={{ backgroundColor: element.couleur }}
                      />
                      <span className="element-text">{element.texte}</span>
                    </div>
                    {selectedElement2Ids.includes(element.id) && (
                      <div className="rating-selector">
                        <select
                          value={ratings[element.id] || 1}
                          onChange={(e) => handleRatingChange(element.id, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <option value={1}>1</option>
                          <option value={2}>2</option>
                          <option value={3}>3</option>
                        </select>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="modal-section">
            <h3>{t.notesSection}</h3>
            <textarea
              className="notes-textarea"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t.notesPlaceholder}
              rows={4}
            ></textarea>
          </div>
          
          <div className="modal-actions">
            <button type="button" className="cancel-button" onClick={onClose}>
              {t.cancel}
            </button>
            <button type="submit" className="save-button">
              {t.save}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DayModal;